import {RankedModeTypes} from '../../../../shared/GameEnums.ts'
import {type LeaderboardTableTabData, type LeaderboardRowData, type LeaderboardStickyRowData, type LeaderboardTabData, type RankedSeasonData} from '../../../../shared/leaderboard_data/LeaderboardInterfaces.ts'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper.ts'
import {UILeaderboardTableController} from '../../../shared/scripts/ui_leaderboard/UILeaderboardTableController.ts'
import {UILeaderboardStickyRowController} from '../../../shared/scripts/ui_leaderboard/UILeaderboardStickyRowController.ts'
import {LeaderboardConstants} from '../../../../shared/leaderboard_data/LeaderboardConstants.ts'
import {UIPopupController} from '../../../shared/scripts/ui_popup/UIPopupController.ts'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints.ts'
import {UIWLeaderboardCTAController} from './UIWLeaderboardCTAController.ts'
import {TimeUtils} from '../../../../shared/utils/TimeUtils.ts'
import {LeaderboardCategoryStringID} from '../../../../shared/leaderboard_data/LeaderboardEnums.ts'
import {clientI18n, clientIntl} from '../../../shared/scripts/ClientI18n.ts'
import {type TimeoutId} from '@colonist/types'

import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils.ts'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables.ts'
import {SeasonProgress, SearchPlayer, TableTabs, RankedPageTabsContainer, RankedPageTabIds} from '@colonist/ui-platform'
import {UILeaderboardUtils} from '../../../shared/scripts/ui_leaderboard/UILeaderboardUtils.ts'
import {type UIReactComponentViewOf, mountUIReactComponent} from '../../../shared/scripts/ui_platform/mountUIReactComponent.tsx'

interface LeaderboardHistoryState {
    rankedModeType: RankedModeTypes
    leaderboardUrl: string
    searchTerm: string
}

export class UIWLeaderboardController {
    protected leaderboardUrl: string = LeaderboardConstants.globalLeaderboardUrl
    private rankedModeType: RankedModeTypes = RankedModeTypes.Classic4P
    private tableTabs: LeaderboardTableTabData[] = []
    private readonly tableController: UILeaderboardTableController
    protected stickyRowController: UILeaderboardStickyRowController
    private readonly rankedTabsView: UIReactComponentViewOf<typeof RankedPageTabsContainer>
    private readonly tableTabsView: UIReactComponentViewOf<typeof TableTabs>
    private readonly seasonProgressView: UIReactComponentViewOf<typeof SeasonProgress>
    private readonly searchPlayerView: UIReactComponentViewOf<typeof SearchPlayer>
    protected ctaController: UIWLeaderboardCTAController
    protected searchTerm: string
    private enabledTabIds: string[] = [RankedPageTabIds.Classic4P, RankedPageTabIds.Classic1v1, RankedPageTabIds.CitiesAndKnights4P]
    private showRushTab: boolean = false
    private isLoadingDataAfterScroll: boolean = false
    private waitForBackend: TimeoutId | undefined

    constructor() {
        UIWLeaderboardController.featureCheck()
        this.stickyRowController = new UILeaderboardStickyRowController()
        this.tableController = new UILeaderboardTableController(() => this.fetchScrollData(), this.stickyRowController)
        this.rankedTabsView = mountUIReactComponent('leaderboard_ranked_tabs', RankedPageTabsContainer)
        this.tableTabsView = mountUIReactComponent('leaderboard_tabs', TableTabs)
        this.seasonProgressView = mountUIReactComponent('leaderboard_season_progress', SeasonProgress)
        this.searchPlayerView = mountUIReactComponent('leaderboard_search', SearchPlayer)
        this.initCTAController()

        this.initiateParametersFromUrl()
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
        void this.initRushTab()

        this.renderSearchPlayer()
        this.renderRankedTabs()
        this.stickyRowController.setSearchValue(this.searchTerm)

        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, this.searchTerm, false)
        window.addEventListener('popstate', (event: PopStateEvent) => this.onPopState(event))
    }

    protected initCTAController() {
        this.ctaController = new UIWLeaderboardCTAController()
    }

    private onSearchChange(searchValue: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, searchValue)
        void this.fetchMyStickyRowData()
        this.stickyRowController.setSearchValue(searchValue)
        this.tableController.updateStickyRowStyle()
    }

    protected onRankedModeChange(rankedMode: RankedModeTypes) {
        this.switchRankingCategoryTypeData(rankedMode, this.leaderboardUrl, this.searchTerm)
        void this.fetchMyStickyRowData()
    }

    private onHorizontalTabChange(leaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, leaderboardUrl, this.searchTerm)
        if(leaderboardUrl == LeaderboardConstants.globalLeaderboardUrl) void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    private onLeaderboardUrlChange(newLeaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, newLeaderboardUrl, this.searchTerm)
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    protected initiateParametersFromUrl() {
        const url = new URL(window.location.href)
        const pathSegments = url.pathname.split('/')
        const leaderboardPathSegmentIndex = pathSegments.indexOf('leaderboards')
        const rankedModeType = pathSegments[leaderboardPathSegmentIndex + 1]
        const leaderboardUrl = pathSegments.slice(leaderboardPathSegmentIndex + 2).join('/')
        if(RankedModeTypes[rankedModeType] !== undefined) this.rankedModeType = RankedModeTypes[rankedModeType]
        this.leaderboardUrl = leaderboardUrl ?? LeaderboardConstants.globalLeaderboardUrl
        this.searchTerm = url.searchParams.get('q') ?? ''
    }

    private onPopState(event: PopStateEvent) {
        const state: LeaderboardHistoryState = event.state

        if(state == undefined) {
            this.initiateParametersFromUrl()
            this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, this.searchTerm, false)
            return
        }

        this.switchRankingCategoryTypeData(state.rankedModeType, state.leaderboardUrl, state.searchTerm, false)
    }

    private getUrl(): string {
        const searchQuery = this.searchTerm === '' ? '' : `?q=${encodeURIComponent(this.searchTerm)}`
        const thisUrl = this.leaderboardUrl ? `/${this.leaderboardUrl}` : ''
        return `${clientIntl.getLanguagePathPrefix()}/leaderboards/${RankedModeTypes[this.rankedModeType]}${thisUrl}${searchQuery}`
    }

    protected switchRankingCategoryTypeData(mode: RankedModeTypes, regionId: string, search: string, push: boolean = true) {
        this.rankedModeType = mode
        this.leaderboardUrl = regionId
        this.searchTerm = search

        if(!UIUtilsEjsVariables.isUserOnDiscord) {
            this.updateState(push)
            this.updateBrowserCaption()
        }

        this.renderRankedTabs()
        this.renderSearchPlayer()
        this.renderHorizontalTabs()
        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.searchTerm == LeaderboardConstants.emptyUsernameSearch ? void this.fetchScrollData() : void this.fetchSearchData()
    }

    private updateBrowserCaption() {
        const thisUrl = this.leaderboardUrl ? `/${this.leaderboardUrl}` : ''
        const pageName = `${RankedModeTypes[this.rankedModeType]}${thisUrl}`
        document.title = clientI18n.t('strings:leaderboardPage.pageHeadInfo.title', {pageName: pageName, interpolation: {escapeValue: false}})
    }

    private updateState(push: boolean) {
        const newState: LeaderboardHistoryState = {rankedModeType: this.rankedModeType, leaderboardUrl: this.leaderboardUrl, searchTerm: this.searchTerm}
        push ? history.pushState(newState, '', this.getUrl()) : history.replaceState(newState, '', this.getUrl())
    }

    private async fetchScrollData() {
        const isActiveSearchOngoing = this.searchTerm != '' && this.tableController.getRowCount() > 0
        if(isActiveSearchOngoing) return

        if(this.isLoadingDataAfterScroll) return
        this.startWaitingForBackendToRespond()
        const startRank = this.tableController.getRowCount() + 1
        const endRank = startRank + LeaderboardConstants.maximumLeaderboardAPIRankingRangeLimit - 1

        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.data(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            start: startRank,
            end: endRank,
            search: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        if(this.leaderboardUrl == LeaderboardCategoryStringID.Friends && data.json.length == 0) {
            this.tableController.displayNoContentMessage()
            return
        }

        const leaderboardRowData = data.json as LeaderboardRowData[]
        if(leaderboardRowData.length == 0) return
        if(!this.isReturnedDataInValidOrder(leaderboardRowData)) {
            await this.fetchScrollData()
            return
        }

        this.tableController.updateRankingData(leaderboardRowData, (leaderboardUrl: string) => this.onLeaderboardUrlChange(leaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private isReturnedDataInValidOrder(leaderboardRowData: LeaderboardRowData[]): boolean {
        if(this.leaderboardUrl == LeaderboardCategoryStringID.Friends) return true
        const firstRankRetured = leaderboardRowData[0].rank as number
        const expectedFirstRankReturned = this.tableController.getRowCount() + 1
        if(firstRankRetured == expectedFirstRankReturned) return true
        return false
    }

    private async fetchSearchData() {
        this.startWaitingForBackendToRespond()
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.usernameSearch(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            usernameSearchInput: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.tableController.updateRankingData(data.json as LeaderboardRowData[], (leaderboardUrl: string) => this.onLeaderboardUrlChange(leaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private startWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = true
        const maxTimeToWaitForBackend = TimeUtils.MILLISECONDS
        this.waitForBackend = setTimeout(() => {
            this.stopWaitingForBackendToRespond()
        }, maxTimeToWaitForBackend)
    }

    private stopWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = false
        if(this.waitForBackend != undefined) clearTimeout(this.waitForBackend)
    }

    private async fetchNewTabs() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.tabs(this.leaderboardUrl), undefined)
        if(response == undefined) return
        const leaderboardTabData = response.json as LeaderboardTabData
        this.updateTabData(leaderboardTabData)
    }

    private renderSeasonProgress(seasonData: RankedSeasonData) {
        this.seasonProgressView.render({
            startDate: new Date(seasonData.startDate),
            endDate: new Date(seasonData.endDate),
            size: 'md',
        })
    }

    private updateTabData(leaderboardTabData: LeaderboardTabData) {
        this.tableTabs = leaderboardTabData.tableTabs
        this.renderHorizontalTabs()
        this.enabledTabIds = leaderboardTabData.activeRankedModeTypes.map((m) => RankedModeTypes[m])
        this.renderRankedTabs()
        if(leaderboardTabData.activeSeasonData != undefined) {
            this.renderSeasonProgress(leaderboardTabData.activeSeasonData)
        }
    }

    private renderRankedTabs() {
        this.rankedTabsView.render(UILeaderboardUtils.getRankedTabsProps({
            rankedModeType: this.rankedModeType,
            enabledTabIds: this.enabledTabIds,
            showRushTab: this.showRushTab,
            onRankedModeChange: (mode) => this.onRankedModeChange(mode),
        },
        {switchToHorizontalOnMediumScreensAndBelow: true, compactOnMediumScreensAndBelow: true}))
    }

    private async initRushTab() {
        if(await UILeaderboardUtils.shouldShowRushTab()) {
            this.showRushTab = true
            this.renderRankedTabs()
        }
    }

    private renderSearchPlayer() {
        this.searchPlayerView.render({
            flatBottom: true,
            searchOnClear: true,
            defaultValue: this.searchTerm,
            onSearch: (searchValue: string) => this.onSearchChange(searchValue),
        })
    }

    private renderHorizontalTabs() {
        this.tableTabsView.render({
            size: 'md',
            switchToDropdownOnMediumScreens: true,
            activeTabId: this.leaderboardUrl,
            tabs: this.tableTabs.map((tab) => ({id: tab.leaderboardUrl, label: UILeaderboardUtils.tLeaderboardTabName(tab.label)})),
            onTabChange: (tabId: string) => this.onHorizontalTabChange(tabId),
        })
    }

    protected async fetchMyStickyRowData() {
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.stickyRowData(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined)
        if(data == undefined) return

        const myStickyRowData = data.json as LeaderboardStickyRowData | ''
        myStickyRowData == '' ? this.stickyRowController.clearStickyRowData() : this.stickyRowController.updateStickyRowData(myStickyRowData, this.tableController.tableContainer.clientHeight)
    }

    private static featureCheck() {
        const div = document.createElement('div')
        if(div.replaceChildren == undefined) {
            UIPopupController.create({key: 'strings:leaderboardPage.updateBrowser.title'}, {key: 'strings:leaderboardPage.updateBrowser.body'},
                () => navigateToPage('/'), () => navigateToPage('/'), false)
            throw new Error('Failed to create leaderboard: Browser does not support required features.')
        }
    }
}
